import { useEffect } from "react";

type PageMetaProps = {
  title: string;
  description?: string;
  canonicalPath?: string;
};

function ensureMetaTag(name: string) {
  let created = false;
  let element = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
    created = true;
  }

  return { element, created };
}

function ensureCanonicalLink() {
  let created = false;
  let element = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
    created = true;
  }

  return { element, created };
}

export default function PageMeta({ title, description, canonicalPath }: PageMetaProps) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    let descriptionCleanup: (() => void) | undefined;
    if (description) {
      const { element, created } = ensureMetaTag("description");
      const previousDescription = element.getAttribute("content");
      element.setAttribute("content", description);
      descriptionCleanup = () => {
        if (created) {
          element.remove();
          return;
        }
        if (previousDescription === null) {
          element.removeAttribute("content");
          return;
        }
        element.setAttribute("content", previousDescription);
      };
    }

    let canonicalCleanup: (() => void) | undefined;
    if (canonicalPath) {
      const { element, created } = ensureCanonicalLink();
      const previousHref = element.getAttribute("href");
      element.setAttribute("href", new URL(canonicalPath, window.location.origin).toString());
      canonicalCleanup = () => {
        if (created) {
          element.remove();
          return;
        }
        if (previousHref === null) {
          element.removeAttribute("href");
          return;
        }
        element.setAttribute("href", previousHref);
      };
    }

    return () => {
      document.title = previousTitle;
      descriptionCleanup?.();
      canonicalCleanup?.();
    };
  }, [canonicalPath, description, title]);

  return null;
}
